<template>
    <div>
        <v-card
            class="timesheet-summary mb-5"
            outlined
        >
            <v-card-text class="d-flex flex-wrap align-center py-5">
                <v-avatar
                    class="mr-4"
                    color="primary"
                    size="44"
                >
                    <v-icon dark>
                        mdi-hammer-wrench
                    </v-icon>
                </v-avatar>
                <div class="mr-8">
                    <div class="total-units">
                        {{ workedTicketCount }}
                    </div>
                    <div class="text-caption text--secondary">
                        Tickets Worked Today
                    </div>
                </div>
                <div
                    class="unit-card summary-hover-target"
                    :class="{
                        'summary-hover-target--active':
                            summaryHighlightKey === 'total',
                    }"
                    aria-label="Highlight all time rows"
                    tabindex="0"
                    @blur="clearSummaryHighlight('total')"
                    @focus="setSummaryHighlight('total')"
                    @mouseenter="setSummaryHighlight('total')"
                    @mouseleave="clearSummaryHighlight('total')"
                >
                    <strong>{{ formattedTotalTime }}</strong>
                    <span>Total Time</span>
                </div>
                <div
                    class="unit-card summary-hover-target"
                    :class="{
                        'summary-hover-target--active':
                            summaryHighlightKey === 'tracked',
                    }"
                    aria-label="Highlight tracked ticket rows"
                    tabindex="0"
                    @blur="clearSummaryHighlight('tracked')"
                    @focus="setSummaryHighlight('tracked')"
                    @mouseenter="setSummaryHighlight('tracked')"
                    @mouseleave="clearSummaryHighlight('tracked')"
                >
                    <strong>{{ formattedTrackedTime }}</strong>
                    <span>Tracked Time</span>
                </div>
                <div
                    v-if="breaks.length"
                    class="unit-card summary-hover-target"
                    :class="{
                        'summary-hover-target--active':
                            summaryHighlightKey === 'breaks',
                    }"
                    aria-label="Highlight break rows"
                    tabindex="0"
                    @blur="clearSummaryHighlight('breaks')"
                    @focus="setSummaryHighlight('breaks')"
                    @mouseenter="setSummaryHighlight('breaks')"
                    @mouseleave="clearSummaryHighlight('breaks')"
                >
                    <strong>{{ formattedBreakTime }}</strong>
                    <span>Break Time</span>
                </div>
                <div
                    v-for="ratePeriod in ratePeriodSummaries"
                    :key="ratePeriod.key"
                    class="unit-card summary-hover-target"
                    :class="{
                        'summary-hover-target--active': summaryHighlightKey
                            === getRateSummaryHighlightKey(ratePeriod.key),
                    }"
                    :aria-label="`Highlight ${ratePeriod.label} ticket rows`"
                    tabindex="0"
                    @blur="clearSummaryHighlight(
                        getRateSummaryHighlightKey(ratePeriod.key)
                    )"
                    @focus="setSummaryHighlight(
                        getRateSummaryHighlightKey(ratePeriod.key)
                    )"
                    @mouseenter="setSummaryHighlight(
                        getRateSummaryHighlightKey(ratePeriod.key)
                    )"
                    @mouseleave="clearSummaryHighlight(
                        getRateSummaryHighlightKey(ratePeriod.key)
                    )"
                >
                    <strong>{{ ratePeriod.duration }}</strong>
                    <span>{{ ratePeriod.label }}</span>
                </div>
                <div
                    class="unit-card error-card summary-hover-target"
                    :class="{
                        'summary-hover-target--active':
                            summaryHighlightKey === 'cancelled',
                    }"
                    aria-label="Highlight cancelled ticket rows"
                    tabindex="0"
                    @blur="clearSummaryHighlight('cancelled')"
                    @focus="setSummaryHighlight('cancelled')"
                    @mouseenter="setSummaryHighlight('cancelled')"
                    @mouseleave="clearSummaryHighlight('cancelled')"
                >
                    <strong>{{ cancelledJobs }}</strong>
                    <span>Cancelled</span>
                </div>
                <v-spacer />
                <v-btn
                    v-if="!submitted"
                    color="primary"
                    :disabled="submitting"
                    depressed
                    :loading="submitting"
                    @click="$emit('submit')"
                >
                    <v-icon
                        left
                        small
                    >
                        mdi-send
                    </v-icon>
                    Submit Timesheet
                </v-btn>
            </v-card-text>
        </v-card>

        <v-alert
            v-if="submitted"
            class="mb-5"
            color="success"
            outlined
            type="success"
        >
            Timesheet submitted. This day is now read-only.
        </v-alert>

        <div class="text-h6 mb-3">
            Ticket Breakdown
        </div>
        <v-card outlined>
            <v-simple-table>
                <thead>
                    <tr>
                        <th>Ticket</th>
                        <th>Billing Code</th>
                        <th>Status</th>
                        <th>Rate</th>
                        <th class="text-right">
                            Duration
                        </th>
                    </tr>
                </thead>
                <tbody>
                    <tr
                        v-for="row in breakdownRows"
                        :key="row.key"
                        :class="[
                            'ticket-breakdown-row',
                            {
                                'breakdown-row--highlighted':
                                    isTicketRowHighlighted(row),
                            },
                        ]"
                        :style="{
                            '--job-timeline-color': row.timelineColor,
                        }"
                    >
                        <td>{{ row.ticket }}</td>
                        <td>{{ row.billingCode }}</td>
                        <td>
                            <v-chip
                                :color="row.status === 'cancelled' ? 'error' : 'success'"
                                label
                                outlined
                                x-small
                            >
                                {{ row.status === 'cancelled' ? 'Cancelled' : 'Completed' }}
                            </v-chip>
                        </td>
                        <td>{{ row.rateLabel }}</td>
                        <td class="text-right">
                            {{ row.duration }}
                        </td>
                    </tr>
                </tbody>
            </v-simple-table>
        </v-card>

        <template v-if="breaks.length">
            <div class="text-h6 mb-3 mt-5">
                Breaks
            </div>
            <v-card outlined>
                <v-simple-table>
                    <thead>
                        <tr>
                            <th>Type</th>
                            <th>Started</th>
                            <th>Ended</th>
                            <th>Duration</th>
                            <th>Billing</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr
                            v-for="breakEntry in breakRows"
                            :key="breakEntry.id"
                            :class="[
                                'break-breakdown-row',
                                {
                                    'breakdown-row--highlighted':
                                        isBreakRowHighlighted(breakEntry),
                                },
                            ]"
                            :style="{
                                '--break-timeline-color': breakTimelineColor,
                            }"
                        >
                            <td>{{ breakEntry.label }}</td>
                            <td>{{ breakEntry.startedAt }}</td>
                            <td>{{ breakEntry.endedAt }}</td>
                            <td>{{ breakEntry.duration }}</td>
                            <td>Non-billable</td>
                        </tr>
                    </tbody>
                </v-simple-table>
            </v-card>
        </template>
    </div>
</template>

<script>
export default {
    name: 'AdvancedBillingTimesheet',
    props: {
        breaks: {
            type: Array,
            default: () => [],
        },
        breakTimelineColor: {
            type: String,
            default: '#8D6E63',
        },
        jobs: {
            type: Array,
            required: true,
        },
        elapsedSeconds: {
            type: Number,
            default: 0,
        },
        highlightedRowKey: {
            type: String,
            default: null,
        },
        submitted: {
            type: Boolean,
            default: false,
        },
        submitting: {
            type: Boolean,
            default: false,
        },
        timelineColors: {
            type: Object,
            default: () => ({}),
        },
    },
    data: () => ({
        summaryHighlightKey: null,
    }),
    computed: {
        cancelledJobs() {
            return this.jobs.filter(job => job.status === 'cancelled').length;
        },
        breakdownRows() {
            return this.jobs.flatMap(job => {
                if(Array.isArray(job.allocations) && job.allocations.length) {
                    return job.allocations.map((allocation, index) => ({
                        key: `${job.id}-${index}`,
                        ticket: job.ticketNumber || job.id,
                        billingCode: allocation.billingCode || 'Not assigned',
                        status: job.status,
                        highlightKey: `job:${job.id}`,
                        rateKey: allocation.ratePeriodId
                            || allocation.rateLabel
                            || 'Not assigned',
                        timelineColor: this.timelineColors[job.id]
                            || 'transparent',
                        rateLabel: allocation.rateLabel || 'Not assigned',
                        seconds: Number(allocation.seconds) || 0,
                        duration: this.formatDuration(allocation.seconds),
                    }));
                }
                return [{
                    key: job.id,
                    ticket: job.ticketNumber || job.id,
                    billingCode: job.billingCode || 'Not assigned',
                    status: job.status,
                    highlightKey: `job:${job.id}`,
                    rateKey: job.ratePeriodId
                        || job.rateLabel
                        || 'Not assigned',
                    timelineColor: this.timelineColors[job.id]
                        || 'transparent',
                    rateLabel: job.rateLabel || 'Not assigned',
                    seconds: Number(job.trackedSeconds) || 0,
                    duration: this.formatDuration(job.trackedSeconds),
                }];
            });
        },
        breakRows() {
            return this.breaks.map(breakEntry => ({
                ...breakEntry,
                highlightKey: `break:${breakEntry.id}`,
                label: breakEntry.label || 'Break',
                startedAt: this.formatTime(breakEntry.startedAt),
                endedAt: breakEntry.endedAt
                    ? this.formatTime(breakEntry.endedAt)
                    : 'In progress',
                duration: this.formatDuration(this.getBreakSeconds(breakEntry)),
            }));
        },
        formattedBreakTime() {
            return this.formatDuration(this.breaks.reduce(
                (total, breakEntry) => (
                    total + this.getBreakSeconds(breakEntry)
                ),
                0
            ));
        },
        formattedTrackedTime() {
            const seconds = Math.round(this.jobs.reduce(
                (total, job) => total + (Number(job.trackedSeconds) || 0),
                0
            ));
            return this.formatDuration(seconds);
        },
        formattedTotalTime() {
            return this.formatDuration(this.elapsedSeconds);
        },
        ratePeriodSummaries() {
            const ratePeriods = new Map();
            const capturedSegments = new Set();
            const addDuration = (job, allocation = job) => {
                const label = String(
                    allocation.rateLabel || 'Not assigned'
                ).trim() || 'Not assigned';
                const key = allocation.ratePeriodId || label;
                const allocationSeconds = Number(allocation.seconds);
                const seconds = Number.isFinite(allocationSeconds)
                    ? allocationSeconds
                    : Number(job.trackedSeconds) || 0;
                const segmentKey = [
                    job.id,
                    key,
                    allocation.startedAt || '',
                    allocation.endedAt || '',
                    seconds,
                ].join('|');
                if(capturedSegments.has(segmentKey)) {
                    return;
                }
                capturedSegments.add(segmentKey);
                const current = ratePeriods.get(key) || {
                    key,
                    label,
                    seconds: 0,
                };
                current.seconds += seconds;
                ratePeriods.set(key, current);
            };
            this.jobs.forEach(job => {
                if(Array.isArray(job.allocations) && job.allocations.length) {
                    job.allocations.forEach(allocation => (
                        addDuration(job, allocation)
                    ));
                    return;
                }
                addDuration(job);
            });
            return [...ratePeriods.values()].map(ratePeriod => ({
                ...ratePeriod,
                duration: this.formatDuration(ratePeriod.seconds),
            }));
        },
        workedTicketCount() {
            return new Set(
                this.jobs
                    .filter(job => job.status === 'completed')
                    .map(job => job.ticketNumber || job.ticket || job.id)
            ).size;
        },
    },
    methods: {
        clearSummaryHighlight(key) {
            if(this.summaryHighlightKey === key) {
                this.summaryHighlightKey = null;
            }
        },
        formatDuration(seconds) {
            const totalSeconds = Math.max(0, Number(seconds) || 0);
            const hours = Math.floor(totalSeconds / 3600);
            const minutes = Math.floor((totalSeconds % 3600) / 60);
            return `${String(hours).padStart(2, '0')}:`
                + `${String(minutes).padStart(2, '0')}`;
        },
        formatTime(value) {
            return new Intl.DateTimeFormat('en-US', {
                hour: 'numeric',
                minute: '2-digit',
            }).format(new Date(value));
        },
        getBreakSeconds(breakEntry) {
            if(!breakEntry.startedAt || !breakEntry.endedAt) {
                return 0;
            }
            return Math.max(0, Math.round(
                (Date.parse(breakEntry.endedAt)
                    - Date.parse(breakEntry.startedAt))
                / 1000
            ));
        },
        getRateSummaryHighlightKey(rateKey) {
            return `rate:${rateKey}`;
        },
        isBreakRowHighlighted(breakEntry) {
            return this.highlightedRowKey === breakEntry.highlightKey
                || ['breaks', 'total'].includes(this.summaryHighlightKey);
        },
        isTicketRowHighlighted(row) {
            if(this.highlightedRowKey === row.highlightKey) {
                return true;
            }
            if(this.summaryHighlightKey === 'tracked') {
                return row.status !== 'cancelled' && row.seconds > 0;
            }
            if(this.summaryHighlightKey === 'total') {
                return row.status !== 'cancelled' && row.seconds > 0;
            }
            if(this.summaryHighlightKey === 'cancelled') {
                return row.status === 'cancelled';
            }
            return this.summaryHighlightKey
                === this.getRateSummaryHighlightKey(row.rateKey);
        },
        setSummaryHighlight(key) {
            this.summaryHighlightKey = key;
        },
    },
};
</script>

<style scoped>
.total-units {
    font-size: 1.75rem;
    font-weight: 500;
}

.unit-card {
    border: 1px solid rgba(128, 128, 128, .28);
    border-radius: 10px;
    display: flex;
    flex-direction: column;
    margin: 4px 8px 4px 0;
    min-width: 118px;
    padding: 10px 14px;
}

.unit-card strong {
    color: var(--v-primary-base);
    font-size: 1.35rem;
}

.unit-card span {
    font-size: .65rem;
    letter-spacing: .08em;
    opacity: .62;
    text-transform: uppercase;
}

.summary-hover-target {
    cursor: default;
    transition: background-color .15s ease, border-color .15s ease;
}

.summary-hover-target:hover,
.summary-hover-target:focus,
.summary-hover-target--active {
    background-color: rgba(128, 128, 128, .14);
    border-color: var(--v-primary-base);
    outline: none;
}

.error-card strong {
    color: var(--v-error-base);
}

.ticket-breakdown-row td:first-child {
    box-shadow: inset 4px 0 0 var(--job-timeline-color);
}

.break-breakdown-row td:first-child {
    box-shadow: inset 4px 0 0 var(--break-timeline-color);
}

.breakdown-row--highlighted {
    background-color: rgba(128, 128, 128, .22);
}

</style>
